import { isAxiosError } from 'axios';
import { reportError } from '../services/errorReporting';

const DEDUP_TTL_MS = 2000;
const DEDUP_MAX_ENTRIES = 20;
const dedupBuffer = new Map<string, number>();

let registeredSource = 'frontend';

function extractErrorMessage(reason: unknown): string {
    if (isAxiosError(reason)) {
        const data = reason.response?.data as Record<string, unknown> | undefined;
        if (data) {
            if (typeof data.message === 'string') return data.message;
            if (typeof data.error === 'string') return data.error;
        }
        return reason.message;
    }
    if (reason instanceof Error) {
        return reason.message;
    }
    if (typeof reason === 'string') return reason;
    if (reason && typeof reason === 'object') {
        const obj = reason as Record<string, unknown>;
        if (typeof obj.message === 'string') return obj.message;
        if (typeof obj.type === 'string') return obj.type;
    }
    if (reason === undefined) return 'Rejected with undefined';
    if (reason === null) return 'Rejected with null';
    try {
        return `Unknown rejection (${typeof reason}): ${JSON.stringify(reason)}`;
    } catch {
        return `Unknown rejection (${typeof reason})`;
    }
}

function isDuplicate(key: string): boolean {
    const now = Date.now();

    // Always evict expired entries to prevent stale buildup
    for (const [k, ts] of dedupBuffer) {
        if (now - ts >= DEDUP_TTL_MS) dedupBuffer.delete(k);
    }

    if (dedupBuffer.has(key)) return true;
    if (dedupBuffer.size >= DEDUP_MAX_ENTRIES) return true;

    dedupBuffer.set(key, now);
    return false;
}

function buildDedupKey(message: string, stack?: string): string {
    if (!stack) return message;
    const frames = stack.split('\n').slice(0, 3).join('\n');
    return `${message.slice(0, 100)}::${frames.slice(0, 200)}`;
}

function initializeErrorHandlers(source: string): void {
    try {
        registeredSource = source;

        // Use addEventListener to avoid overwriting other error handlers
        window.addEventListener('error', (event: ErrorEvent) => {
            try {
                const message = event.error?.message ?? event.message ?? 'Unknown error';
                const stack = event.error?.stack;
                const key = buildDedupKey(message, stack);
                if (isDuplicate(key)) return;

                reportError({
                    source: registeredSource,
                    message,
                    stack,
                    url: window.location.href,
                    pathname: window.location.pathname,
                    userAgent: navigator.userAgent,
                    errorType: 'onerror'
                });
            } catch {
                // Error reporting must never interfere with the app
            }
        });

        window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
            try {
                const reason = event.reason;
                const message = extractErrorMessage(reason);
                const stack = reason instanceof Error ? reason.stack : undefined;
                const key = buildDedupKey(message, stack);
                if (isDuplicate(key)) return;

                reportError({
                    source: registeredSource,
                    message,
                    stack,
                    url: window.location.href,
                    pathname: window.location.pathname,
                    userAgent: navigator.userAgent,
                    errorType: 'unhandledrejection'
                });
            } catch {
                // Error reporting must never interfere with the app
            }
        });
    } catch {
        // Initialization failure must be completely silent
    }
}

function reportErrorFromBoundary(error: Error, componentStack?: string): void {
    try {
        const key = buildDedupKey(error.message, error.stack);
        if (isDuplicate(key)) return;

        reportError({
            source: registeredSource,
            message: error.message,
            stack: error.stack,
            componentStack,
            url: window.location.href,
            pathname: window.location.pathname,
            userAgent: navigator.userAgent,
            errorType: 'react_boundary'
        });
    } catch {
        // Error reporting must never interfere with the app
    }
}

export { initializeErrorHandlers, reportErrorFromBoundary };
